// v16.61.1 (Feb 2026) — Apple 5.1.1(ii). AI consent gate.
//
// Chính sách: Modal HIỆN MỖI LẦN user upload ảnh cần OCR/AI phân tích.
// KHÔNG lưu trạng thái granted/declined ở localStorage, sessionStorage
// hay bất kỳ cache device-level nào. Lý do:
//   - Ngăn kịch bản User A đồng ý → User B đăng nhập cùng thiết bị kế
//     thừa consent cũ mà không được hỏi.
//   - Đảm bảo "explicit informed consent" theo yêu cầu Apple / GDPR:
//     mỗi lần dữ liệu có thể được gửi tới AI service, user phải chủ
//     động đồng ý riêng.
//
// Component chỉ nhận `open`, `onAgree`, `onDecline` — hoàn toàn không có
// side-effect ngoài callback.
import { Robot, Warning, CheckCircle, X } from "@phosphor-icons/react";

export default function AIConsentModal({ open, onAgree, onDecline, onCancel }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[500] bg-black/75 backdrop-blur-sm flex items-center justify-center px-4 py-6"
      data-testid="ai-consent-modal"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="w-full max-w-md bg-[#0F1623] border border-white/12 rounded-2xl shadow-2xl">
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <div className="inline-flex items-center gap-1.5">
            <Robot size={16} weight="fill" className="text-sky-400" />
            <h3 className="font-black text-white text-[14px]">Sử dụng AI để phân tích ảnh</h3>
          </div>
          <button
            type="button"
            onClick={() => (onCancel || onDecline)?.()}
            className="p-1 rounded-md hover:bg-white/10 text-white/60"
            data-testid="ai-consent-close"
          >
            <X size={14} weight="bold" />
          </button>
        </div>

        <div className="p-4 space-y-3 text-[12.5px] text-white/85 leading-relaxed">
          <div className="p-3 rounded-lg bg-sky-500/10 border border-sky-400/30">
            <div className="flex items-start gap-2">
              <Warning size={16} weight="fill" className="text-amber-300 shrink-0 mt-0.5" />
              <div>
                Để chống mạo danh, PickLeague sử dụng dịch vụ AI phân tích ảnh
                bạn tải lên (OCR — nhận diện ký tự, phát hiện logo/watermark).
                Việc này giúp phát hiện ảnh copy từ nguồn khác hoặc ảnh chứa số
                điện thoại/thông tin cá nhân người khác.
              </div>
            </div>
          </div>

          <div>
            <div className="font-black text-white mb-1">📤 Dữ liệu gửi đi</div>
            <ul className="list-disc ml-5 space-y-0.5 text-white/75">
              <li>Chỉ <b className="text-white/90">ảnh bạn vừa chọn</b> cho lần upload này.</li>
              <li>KHÔNG gửi: tên thật, SĐT, email, mật khẩu, hay bất kỳ dữ liệu cá nhân nào khác.</li>
            </ul>
          </div>

          <div>
            <div className="font-black text-white mb-1">🌐 Bên nhận dữ liệu</div>
            <ul className="list-disc ml-5 space-y-0.5 text-white/75">
              <li>Google (dịch vụ Gemini) HOẶC OpenAI (GPT-4o) — chọn tự động theo lưu lượng.</li>
              <li>Trung gian: Emergent LLM Gateway (server tại EU/US).</li>
            </ul>
          </div>

          <div>
            <div className="font-black text-white mb-1">🗑 Quyền của bạn</div>
            <ul className="list-disc ml-5 space-y-0.5 text-white/75">
              <li>Từ chối và upload ảnh bình thường — ảnh KHÔNG được gửi tới AI (BTC có thể duyệt thủ công sau).</li>
              <li>Yêu cầu xoá dữ liệu đã xử lý qua email <code>support@pickleague.vn</code>.</li>
              <li>Consent được hỏi lại mỗi lần bạn upload ảnh mới — không lưu quyết định cũ.</li>
            </ul>
          </div>
        </div>

        {/* v16.60.19 — Bug4: nút to chuẩn (h-12 ≈ 48px, text 15px) dễ bấm trên mobile. */}
        <div className="px-4 py-4 border-t border-white/10 flex flex-col sm:flex-row gap-3">
          <button
            type="button"
            onClick={onDecline}
            className="flex-1 h-12 min-h-[48px] px-5 rounded-xl bg-white/8 hover:bg-white/15 border border-white/15 text-white text-[15px] font-bold transition"
            data-testid="ai-consent-decline"
          >
            Không đồng ý
          </button>
          <button
            type="button"
            onClick={() => onAgree?.()}
            className="flex-1 h-12 min-h-[48px] px-5 rounded-xl bg-gradient-to-r from-sky-500 to-indigo-500 hover:from-sky-600 hover:to-indigo-600 text-white text-[15px] font-black tracking-wide inline-flex items-center justify-center gap-2 transition"
            data-testid="ai-consent-agree"
          >
            <CheckCircle size={18} weight="fill" /> Đồng ý & tiếp tục
          </button>
        </div>
      </div>
    </div>
  );
}
